import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
export function RelationshipDecodeOutput() {
  // In production, this would come from API based on user inputs
  const reportData = {
    reportName: 'Relationship Decode',
    reportType: 'Full Diagnostic Report',
    // Section 1: The Interaction System
    interactionSystem: {
      description: "This relationship operates as a closed-loop system where each person's response stabilizes the dynamic and returns it to a predictable state. The system activates when uncertainty or vulnerability is present. Rather than resolving tension, the interaction reinforces the pattern that created it.",
      mechanics: ['Activation: The system triggers when one person experiences uncertainty, needs reassurance, or attempts to address friction', "Stabilization: Each person's response locks the other into a predictable counter-response, creating a self-reinforcing loop", 'Return to baseline: After temporary relief or escalation, the system returns to its default state without meaningful recalibration']
    },
    // Section 2: Primary Relational Dynamic
    primaryDynamic: {
      name: 'Pursue-withdraw with control negotiation',
      description: 'One person seeks clarity or connection through direct engagement. The other person manages exposure by creating distance or controlling the frame of interaction. Neither response resolves the underlying tension—both reinforce the loop.',
      whatEachProtects: {
        person1: 'You protect against abandonment or being misunderstood by pursuing clarity and connection. The cost: your pursuit activates their withdrawal, which confirms the fear you were trying to prevent.',
        person2: 'They protect against being overwhelmed or losing autonomy by creating distance or controlling interaction terms. The cost: their withdrawal activates your pursuit, which confirms the pressure they were trying to avoid.'
      },
      tradeoff: 'In exchange for temporary emotional safety, both people trade the possibility of genuine recalibration. The system prioritizes predictability over resolution.'
    },
    // Section 3: How the Pattern Runs
    patternMechanics: {
      trigger: 'The pattern activates when one person experiences uncertainty, attempts to address tension, or seeks reassurance. The trigger is not the content of the issue—it is the presence of vulnerability or the need for clarity.',
      yourResponse: 'You typically respond by explaining, seeking connection, or attempting to resolve the tension directly. This feels necessary because silence or distance registers as rejection or abandonment.',
      theirResponse: 'They typically respond by withdrawing, deflecting, or controlling the frame of the conversation. This feels necessary because direct engagement registers as pressure or loss of control.',
      shortTermRelief: 'Temporary reduction in tension occurs when one person accommodates or when the interaction ends. The system interprets this as resolution, but the underlying dynamic remains unchanged.',
      longTermCost: 'Each cycle reinforces the pattern. Your pursuit becomes more urgent. Their withdrawal becomes more automatic. The range of available responses narrows. Intimacy requires vulnerability, but vulnerability activates the protection mechanism in both people.'
    },
    // Section 4: Secondary Dynamic Under Strain
    secondaryDynamic: {
      description: 'When the primary pattern runs too long or fails to provide relief, a secondary dynamic emerges. This is not a separate issue—it is what happens when the system is under sustained pressure.',
      whatEmerges: 'Escalation or emotional shutdown. If pursuit intensifies, the interaction may escalate into conflict where both people defend their position rather than address the underlying loop. If withdrawal deepens, emotional numbing occurs—both people remain physically present but relationally disengaged.',
      mechanics: 'The secondary dynamic is more costly than the primary one. Escalation creates visible rupture but no recalibration. Shutdown creates the appearance of peace but prevents any meaningful contact.',
      recognitionPoint: 'You know the secondary dynamic is active when the interaction feels either explosive or completely flat—when there is no middle ground between conflict and silence.'
    },
    // Section 5: Why This System Exists
    systemOrigin: {
      originalProblem: 'This dynamic likely formed as a solution to incompatible needs for connection and autonomy. One person learned that pursuit was necessary to maintain connection. The other person learned that distance was necessary to maintain autonomy.',
      whyItWorked: "In its original context, this pattern provided a workable compromise. Pursuit ensured the relationship did not dissolve into neglect. Withdrawal ensured neither person was overwhelmed by the other's needs. The system was functional—not optimal, but stable.",
      whyItPersists: 'The pattern persists because it continues to provide short-term relief. Each time pursuit is met with eventual engagement (even reluctant), the pursuer is reinforced. Each time withdrawal is met with eventual backing off, the withdrawer is reinforced. The system does not evaluate whether the pattern still serves—it evaluates whether the pattern still provides predictability.',
      systemLogic: "From the system's perspective, this dynamic is not broken. It is doing exactly what it was designed to do: maintain stability and prevent the relationship from collapsing into chaos or dissolution. The issue is not that the pattern exists—it is that the pattern now operates at the cost of intimacy, clarity, and range."
    },
    // Section 6: What This Dynamic Costs Over Time
    dynamicCosts: {
      emotional: 'Chronic low-level anxiety for the pursuer—connection feels conditional and uncertain. Chronic low-level pressure for the withdrawer—engagement feels like obligation rather than choice. Neither person experiences ease. Both people experience the relationship as effortful.',
      relational: 'Intimacy requires vulnerability, but vulnerability activates the protection mechanism in both people. Conversations that require depth are avoided or derailed. Conflict is either suppressed or explosive—there is no middle ground. The relationship operates in a narrow bandwidth where neither person feels fully seen or fully safe.',
      decision: 'Decisions that require joint clarity are delayed, deferred, or made unilaterally. The pursuer may over-function to compensate for perceived withdrawal. The withdrawer may under-engage to avoid perceived pressure. Neither strategy resolves the underlying dynamic. Over time, the relationship becomes defined by what is avoided rather than what is built together.'
    },
    // Section 7: Where the System Loosens
    systemLoosening: {
      conditions: ['The pattern weakens in contexts where neither person feels evaluated or at risk—when interaction occurs without agenda or expectation', 'The pattern shows gaps when external structure provides clarity—when decisions are made by circumstance rather than negotiation', 'The pattern loosens when both people are engaged in parallel activity rather than direct interaction—when connection occurs through shared focus rather than face-to-face engagement', "The pattern becomes less automatic in moments where one person's response shifts naturally without conscious effort—when pursuit does not escalate or withdrawal does not harden"]
    },
    // Boundary Statement
    boundaries: 'This decode explains the interaction system between two people. It does not judge either person or advise action. Clarity comes from seeing the pattern clearly, not forcing resolution.'
  };
  return <>
      <Helmet data-id="element-4018">
        <title data-id="element-4019">{reportData.reportName} — Output | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-4020" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4021">
        {/* Navigation */}
        <header data-id="element-4022">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-4023">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4024">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-4025">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-4026" />
                <span className="text-lg font-medium tracking-tight" data-id="element-4027">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-4028">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-4029">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-4030">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <main className="pt-32 pb-24 px-6" data-id="element-4031">
          <div className="max-w-4xl mx-auto" data-id="element-4032">
            {/* Page Header */}
            <div className="mb-16" data-id="element-4033">
              <h1 className="text-4xl md:text-5xl font-medium mb-3 leading-tight tracking-tight" data-id="element-4034">
                {reportData.reportName}
              </h1>
              <p className="text-sm text-[#F5F5F5]/50" data-id="element-4035">
                {reportData.reportType}
              </p>
            </div>

            {/* Section 1: The Interaction System */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-4036">
              <div className="mb-8" data-id="element-4037">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-4038">
                  The interaction system between you
                </h2>
              </div>

              <p className="text-[#F5F5F5]/70 leading-relaxed mb-8" data-id="element-4039">
                {reportData.interactionSystem.description}
              </p>

              <div className="space-y-4" data-id="element-4040">
                {reportData.interactionSystem.mechanics.map((item, index) => <div key={index} className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4041">
                    <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4042">{item}</p>
                  </div>)}
              </div>
            </section>

            {/* Section 2: Primary Relational Dynamic */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-4043">
              <div className="mb-8" data-id="element-4044">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-4045">
                  Primary relational dynamic
                </h2>
              </div>

              <div className="mb-8" data-id="element-4046">
                <h3 className="text-xl font-medium text-[#F5F5F5]/90 mb-4" data-id="element-4047">
                  {reportData.primaryDynamic.name}
                </h3>
                <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4048">
                  {reportData.primaryDynamic.description}
                </p>
              </div>

              <div className="space-y-6" data-id="element-4049">
                <div data-id="element-4050">
                  <h4 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-4051">
                    What each person protects
                  </h4>
                  <div className="space-y-4" data-id="element-4052">
                    <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4053">
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4054">
                        {reportData.primaryDynamic.whatEachProtects.person1}
                      </p>
                    </div>
                    <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4055">
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4056">
                        {reportData.primaryDynamic.whatEachProtects.person2}
                      </p>
                    </div>
                  </div>
                </div>

                <div data-id="element-4057">
                  <h4 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-4058">
                    The tradeoff
                  </h4>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4059">
                    {reportData.primaryDynamic.tradeoff}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 3: How the Pattern Runs */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-4060">
              <div className="mb-8" data-id="element-4061">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-4062">
                  How the pattern runs
                </h2>
              </div>

              <div className="space-y-6" data-id="element-4063">
                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4064">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-4065">
                    Trigger
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4066">
                    {reportData.patternMechanics.trigger}
                  </p>
                </div>

                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4067">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-4068">
                    Your response
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4069">
                    {reportData.patternMechanics.yourResponse}
                  </p>
                </div>

                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4070">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-4071">
                    Their response
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4072">
                    {reportData.patternMechanics.theirResponse}
                  </p>
                </div>

                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4073">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-4074">
                    Short-term relief
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4075">
                    {reportData.patternMechanics.shortTermRelief}
                  </p>
                </div>

                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4076">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-4077">
                    Long-term cost
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4078">
                    {reportData.patternMechanics.longTermCost}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 4: Secondary Dynamic Under Strain */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-4079">
              <div className="mb-8" data-id="element-4080">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-4081">
                  Secondary dynamic under strain
                </h2>
              </div>

              <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-4082">
                {reportData.secondaryDynamic.description}
              </p>

              <div className="space-y-6" data-id="element-4083">
                <div data-id="element-4084">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-4085">
                    What emerges
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4086">
                    {reportData.secondaryDynamic.whatEmerges}
                  </p>
                </div>

                <div data-id="element-4087">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-4088">
                    Mechanics
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4089">
                    {reportData.secondaryDynamic.mechanics}
                  </p>
                </div>

                <div data-id="element-4090">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-4091">
                    Recognition point
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4092">
                    {reportData.secondaryDynamic.recognitionPoint}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5: Why This System Exists */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-4093">
              <div className="mb-8" data-id="element-4094">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-4095">
                  Why this system exists
                </h2>
              </div>

              <div className="space-y-6" data-id="element-4096">
                <div data-id="element-4097">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-4098">
                    Original problem
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4099">
                    {reportData.systemOrigin.originalProblem}
                  </p>
                </div>

                <div data-id="element-4100">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-4101">
                    Why it worked
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4102">
                    {reportData.systemOrigin.whyItWorked}
                  </p>
                </div>

                <div data-id="element-4103">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-4104">
                    Why it persists
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4105">
                    {reportData.systemOrigin.whyItPersists}
                  </p>
                </div>

                <div data-id="element-4106">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-4107">
                    System logic
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4108">
                    {reportData.systemOrigin.systemLogic}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 6: What This Dynamic Costs Over Time */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-4109">
              <div className="mb-8" data-id="element-4110">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-4111">
                  What this dynamic costs over time
                </h2>
              </div>

              <div className="space-y-6" data-id="element-4112">
                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4113">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-4114">
                    Emotional cost
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4115">
                    {reportData.dynamicCosts.emotional}
                  </p>
                </div>

                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4116">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-4117">
                    Relational cost
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4118">
                    {reportData.dynamicCosts.relational}
                  </p>
                </div>

                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4119">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-4120">
                    Decision cost
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4121">
                    {reportData.dynamicCosts.decision}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 7: Where the System Loosens */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-4122">
              <div className="mb-8" data-id="element-4123">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-4124">
                  Where the system loosens
                </h2>
              </div>

              <div className="space-y-4" data-id="element-4125">
                {reportData.systemLoosening.conditions.map((condition, index) => <div key={index} className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-4126">
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4127">
                        {condition}
                      </p>
                    </div>)}
              </div>
            </section>

            {/* Section 8: Boundary Statement */}
            <section className="mb-16" data-id="element-4128">
              <div className="border border-[#2B2B2B] p-8" data-id="element-4129">
                <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4130">
                  {reportData.boundaries}
                </p>
              </div>
            </section>
          </div>
        </main>

        {/* Minimal Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" role="contentinfo" data-id="element-4131">
          <div className="max-w-7xl mx-auto text-center" data-id="element-4132">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-4133">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}